<script setup>
import { computed } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { removeParticipantNotarial } from "@/entities/transaction";
import { useConfirm } from "@/shared/ui/alert-dialog";
import SectionGroup from "@/pages/admin/home/notarial_actions/components/ParticipantSectionGroup.vue";
import ParticipantPhoto from "@/pages/admin/home/notarial_actions/components/ParticipantPhoto.vue";
import { Spinner } from "@/shared/ui/spinner";

const { t } = useI18n();
const confirm = useConfirm();

const props = defineProps({
  participants: {
    type: Array,
    default: () => [],
  },
  participantTypes: {
    type: Array,
    default: () => [],
  },
  stepsEntityTypes: {
    type: Object,
    default: () => ({}),
  },
  activeService: {
    type: String,
    default: () => null,
  },
  template: {
    type: Object,
    default: () => ({}),
  },
  selectedParticipant: {
    type: Object,
    default: null,
  },
  isAccessLoading: {
    type: Boolean,
    default: () => false,
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
  requestedServices: {
    type: Map,
    default: () => new Map(),
  },
  canEdit: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  "update-data",
  "select:step",
  "select:participant",
  "edit:participant",
  "insert:saved-fields",
]);

const route = useRoute();

const uuid = computed(() => route.params.id);

async function confirmRemove(action) {
  try {
    await confirm(
      t(
        "Вы действительно хотите открепить участника от нотариального действия?"
      ),
      t("Открепление"),
      {
        confirmButtonText: t("Открепить"),
        cancelButtonText: t("Отмена"),
        type: "danger",
      }
    );

    const participant = props.participants.find((p) => p.uuid === action.uuid);
    if (participant) {
      await removeParticipant(participant);
    }
  } catch {
    // Отменено
  }
}

function getParticipantRoleLabel(role) {
  if (role.participant_type === "INDIVIDUAL") {
    let type = props.participantTypes.find(
      (r) => r.code === role.participant_type_code
    );
    return type?.name || type?.name_kz;
  }
  if (role.participant_type === "LEGAL") {
    let type = props.participantTypes.find(
      (r) => r.code === role.participant_type_code
    );
    return type?.name || type?.name_kz;
  }
  return role.code;
}

async function removeParticipant(participant) {
  let response = await removeParticipantNotarial(uuid.value, participant.uuid);
  if (response?.success) {
    emit("update-data", participant);
  }
}

function changeService(item) {
  emit("select:step", item);
}

function getGroups(participant) {
  const stepList = props.stepsEntityTypes?.[participant.participant_type] || [];
  return [
    {
      title: getParticipantRoleLabel(participant),
      action: props.canEdit
        ? {
            icon: "huge-close",
            uuid: participant.uuid,
          }
        : null,
      key: "external_services",
      expanded: true,
      items: [
        {
          name: "Ручной ввод",
          code: "manual_input",
        },
        ...(participant.service_fields?.manual &&
        Object.keys(participant.service_fields.manual).length > 0
          ? [{ name: t("Сведения о лице"), code: "manual" }]
          : []),
        ...stepList,
        {
          name: "Внутренние базы",
          code: "internal_databases",
        },
        ...(participant.participant_type_code === "NASLEDODATEL"
          ? [{ name: "Информация проверки", code: "show-check-info" }]
          : []),
      ],
    },
  ];
}

function selectParticipant(item) {
  emit("select:participant", item);
}

function editParticipant(participant) {
  emit("edit:participant", participant);
}

// async function copyIdentifier(identifier) {
//   if (!identifier) return;
//   try {
//     await navigator.clipboard.writeText(identifier);
//     toast.success(t("Скопировано"), { duration: 1500 });
//   } catch {
//     // ignore
//   }
// }
</script>

<template>
  <div v-if="participants?.length" class="flex flex-col gap-4">
    <div v-if="loading" class="flex justify-center items-center py-8">
      <Spinner size="sm" />
    </div>

    <template v-else>
      <div
        v-for="item in participants"
        :key="item.id || item.uuid"
        @click="selectParticipant(item)"
      >
        <SectionGroup
          v-for="group in getGroups(item)"
          :key="group.title"
          :group="group"
          :activeService="activeService"
          :participant="item"
          :selected-participant="selectedParticipant"
          :isAccessLoading="isAccessLoading"
          :requestedServices="requestedServices"
          :can-edit="canEdit"
          @change:service="changeService"
          @handleClick="confirmRemove"
          @edit="editParticipant"
          @insert:saved-fields="(p) => emit('insert:saved-fields', p)"
        >
          <template #test>
            <div class="flex flex-col gap-2 pb-3">
              <div class="flex items-center gap-3">
                <ParticipantPhoto
                  v-if="item.person_photo"
                  :photo="item.person_photo"
                  :alt="item.first_name + ' ' + item.last_name"
                />

                <div class="flex flex-col min-w-0">
                  <div class="flex items-center gap-1">
                    <div
                      class="text-foreground text-sm font-semibold line-clamp-2"
                    >
                      <template v-if="item.first_name">
                        {{
                          item.first_name +
                          " " +
                          item.last_name +
                          " " +
                          item?.middle_name
                        }}
                      </template>
                      <template v-else-if="item?.params?.company_name">
                        {{ item.params.company_name || "" }}
                      </template>
                    </div>

                    <el-icon class="text-rose-500 w-4 h-4 opacity-0">
                      <WarningFilled />
                    </el-icon>
                  </div>

                  <div
                    v-if="item.identifier"
                    class="text-muted-foreground text-sm line-clamp-1"
                  >
                    {{ item.identifier }}
                  </div>
                </div>
              </div>
              <slot name="participant-extra" :item="item" />
            </div>
          </template>
        </SectionGroup>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss"></style>
